// ─── Google Calendar & Meet integration — what the settings page says, and where it may go ───
//
// Only the stable codes the Vistaar API returns are turned into text here. A server message, a
// Google error or anything else from a response is never shown as it came.

const GOOGLE_AUTHORIZATION_ORIGIN = 'https://accounts.google.com';
const GOOGLE_MEET_ORIGIN = 'https://meet.google.com';

/** Query parameters the backend's OAuth callback adds to /portal/integration-settings. */
export const CALLBACK_PARAMS = ['google', 'result', 'reason'];

export const MESSAGES = {
  connected: 'Google Calendar connected successfully.',
  accountChanged: 'A different Google account is now connected. Meet links created earlier stay in the previous account.',
  disconnected: 'Google Calendar disconnected.',
  cancelled: 'Google authorization was cancelled.',
  unavailable: 'Google Calendar integration is currently unavailable.',
  reconnect: 'Please reconnect your Google account.',
  connectFailed: 'Google Calendar connection could not be completed. Please try again.',
  connectNotStarted: 'Google Calendar connection could not be started. Please try again.',
  disconnectFailed: 'Google Calendar could not be disconnected. Please try again.',
  statusFailed: 'Google Calendar status could not be loaded. Please try again.',
  refreshFailed: 'Google Calendar status could not be refreshed.',
  noPermission: "You don't have permission to manage this clinic's Google Calendar connection.",
  noAccess: "You don't have access to this clinic's Google Calendar settings.",
  inProgress: "Another change to this clinic's Google connection is in progress. Please try again in a moment.",
  tooManyAttempts: 'Too many connection attempts. Please try again later.',
  unreachable: 'Server unreachable. Please check your connection and try again.',
  // The booking form's Google Meet option, and what is said once a booking went through.
  meetConnectInSettings: 'Connect Google Calendar in Settings to enable Google Meet.',
  meetAskAdminToConnect: 'Google Calendar is not connected. Ask an Admin to connect it to enable Google Meet.',
  meetNeedsReconnect: 'Google Calendar needs to be reconnected.',
  meetAskAdminToReconnect: 'Google Calendar needs to be reconnected. Ask an Admin to reconnect it.',
  meetDisconnecting: 'Google Calendar is being disconnected.',
  meetUnavailable: 'Google Meet is currently unavailable.',
  meetChecking: 'Checking Google Calendar…',
  meetBeingCreated: 'Appointment booked. Google Meet is being created.',
  meetsBeingCreated: 'Appointments booked. Google Meet links are being created.',
  meetNotAdded: 'Appointment booked without Google Meet — Google Calendar is not connected.',
  // One appointment's Google Meet, in its detail panel.
  meetLoading: 'Checking Google Meet…',
  meetStatusFailed: "Google Meet status couldn't be loaded.",
  meetNotEnabled: 'Not enabled for this appointment.',
  meetNotConnected: 'Google Meet is unavailable because Google Calendar is not connected.',
  meetReady: 'Ready',
  meetCreating: 'Creating Google Meet…',
  meetSlow: 'Google Meet is taking longer than usual.',
  meetFailed: "Google Meet couldn't be created.",
  meetFailedAppointmentOver: "Google Meet couldn't be created because the appointment is already over.",
  meetWaitingForConnection: 'Waiting for Google Calendar.',
  meetLinkUnavailable: 'The Google Meet link is not available.',
  meetOtherAccount: 'This Google Meet was created in a previously connected Google account.',
  meetAppointmentCancelled: 'Google Meet was removed because this appointment is cancelled.',
  meetEnabling: 'Google Meet is being created.',
  meetRemoved: 'Google Meet removed.',
  meetLinkCopied: 'Meeting link copied.',
  meetCopyFailed: "The meeting link couldn't be copied.",
  meetNoPermission: "You don't have permission to manage Google Meet for this appointment.",
  meetEnableFailed: 'Google Meet could not be enabled. Please try again.',
  meetRetryFailed: 'Google Meet could not be retried. Please try again.',
  meetRemoveFailed: 'Google Meet could not be removed. Please try again.',
};

// The reasons the callback can report (backend googleCalendarConnectionService / googleOAuthStateService).
// Anything not listed — including a code this page does not know yet — gets the generic message.
const SIGN_IN_EXPIRED = 'The Google sign-in expired or was already used. Please connect again.';
const CALLBACK_REASONS = {
  access_denied: MESSAGES.cancelled,
  google_denied: 'Google did not allow the connection. Please try again.',
  disabled: MESSAGES.unavailable,
  not_configured: MESSAGES.unavailable,
  needs_reconnect: MESSAGES.reconnect,
  invalid_grant: MESSAGES.reconnect,
  insufficient_scope: "Google Calendar access wasn't allowed. Connect again and make sure the Google Calendar permission is ticked.",
  missing_state: SIGN_IN_EXPIRED,
  invalid_state: SIGN_IN_EXPIRED,
  expired_state: SIGN_IN_EXPIRED,
  state_mismatch: SIGN_IN_EXPIRED,
  malformed_callback: SIGN_IN_EXPIRED,
  browser_mismatch: 'Please finish connecting Google Calendar in the same browser you started from.',
  unauthorized: MESSAGES.noPermission,
  connect_in_progress: MESSAGES.inProgress,
  rate_limited: 'Google is busy right now. Please try again in a few minutes.',
};

const SAFE_CODE = /^[a-z_]{1,64}$/;
const knownReason = (reason) =>
  typeof reason === 'string' && SAFE_CODE.test(reason) && Object.prototype.hasOwnProperty.call(CALLBACK_REASONS, reason);

/**
 * What to tell the person who has just come back from Google.
 * @returns {null | { type: 'success'|'error', message: string, note: string|null }}
 */
export function callbackOutcome({ google, result, reason }) {
  if (google === 'connected') {
    return { type: 'success', message: MESSAGES.connected, note: result === 'account_changed' ? MESSAGES.accountChanged : null };
  }
  if (google === 'error') {
    return { type: 'error', message: knownReason(reason) ? CALLBACK_REASONS[reason] : MESSAGES.connectFailed, note: null };
  }
  return null;
}

/** Is there anything from the OAuth callback in these search params? */
export function hasCallbackParams(searchParams) {
  return CALLBACK_PARAMS.some((key) => searchParams.has(key));
}

const httpStatus = (err) => (err && err.response && Number(err.response.status)) || null;
const apiCode = (err) => {
  const body = err && err.response && err.response.data;
  const code = body && body.data && body.data.code;
  return typeof code === 'string' ? code : null;
};
// The API client rejects with { type: 'NETWORK_ERROR' } when no response came back at all.
const isNetworkError = (err) => !!err && err.type === 'NETWORK_ERROR';

/** Loading the connection status failed. */
export function statusErrorMessage(err) {
  if (isNetworkError(err)) return MESSAGES.unreachable;
  if (httpStatus(err) === 403) return MESSAGES.noAccess;
  return MESSAGES.statusFailed;
}

/**
 * Starting a connection failed. null when the app already shows its own notice (a subscription
 * that is read-only or has lapsed answers 402, which the API client announces).
 */
export function connectErrorMessage(err) {
  if (isNetworkError(err)) return MESSAGES.unreachable;
  const status = httpStatus(err);
  const code = apiCode(err);
  if (status === 402) return null;
  if (status === 403) return MESSAGES.noPermission;
  if (status === 409 || code === 'connect_in_progress') return MESSAGES.inProgress;
  if (status === 429) return MESSAGES.tooManyAttempts;
  if (code === 'disabled' || code === 'not_configured' || status === 503) return MESSAGES.unavailable;
  return MESSAGES.connectNotStarted;
}

/** Disconnecting failed. */
export function disconnectErrorMessage(err) {
  if (isNetworkError(err)) return MESSAGES.unreachable;
  const status = httpStatus(err);
  if (status === 403) return MESSAGES.noPermission;
  if (status === 409 || apiCode(err) === 'connect_in_progress') return MESSAGES.inProgress;
  return MESSAGES.disconnectFailed;
}

/**
 * The booking form's Google Meet option, from the clinic's integration status (as the status API
 * returns it). Booking never waits on this: every state but `connected` only means Meet cannot be
 * chosen for this booking.
 *
 * @param {{ loading?: boolean, failed?: boolean, status?: object|null }} query
 * @returns {{ visible: boolean, selectable: boolean, state: string, message: string|null, settingsLink: boolean }}
 *   state: loading | connected | not_connected | needs_reconnect | disconnecting | unavailable
 *   settingsLink: offer "open Settings" — only to someone who may connect Google Calendar
 */
export function meetOption({ loading = false, failed = false, status = null } = {}) {
  const option = (state, message, extra = {}) => ({ visible: true, selectable: false, state, message, settingsLink: false, ...extra });
  if (status && status.enabled === false) {
    // Switched off for this server: nothing to offer, nothing to explain.
    return { visible: false, selectable: false, state: 'unavailable', message: null, settingsLink: false };
  }
  if (!status) return loading && !failed ? option('loading', MESSAGES.meetChecking) : option('unavailable', MESSAGES.meetUnavailable);
  if (!status.available) return option('unavailable', MESSAGES.meetUnavailable);

  const canManage = status.can_manage === true;
  switch (status.status) {
    case 'CONNECTED':
      return { visible: true, selectable: true, state: 'connected', message: null, settingsLink: false };
    case 'NEEDS_RECONNECT':
      return option('needs_reconnect', canManage ? MESSAGES.meetNeedsReconnect : MESSAGES.meetAskAdminToReconnect, { settingsLink: canManage });
    case 'DISCONNECTING':
      return option('disconnecting', MESSAGES.meetDisconnecting);
    default:
      return option('not_connected', canManage ? MESSAGES.meetConnectInSettings : MESSAGES.meetAskAdminToConnect, { settingsLink: canManage });
  }
}

const MEET_REQUESTED = new Set([true, 1, '1', 'true']);

/**
 * What to say about Google Meet once the booking API has accepted appointments. The API's own
 * answer decides: an appointment it kept Meet on has google_meet_enabled = 1; the backend books
 * without Meet when Google Calendar is not connected. null when there is nothing to say.
 *
 * @param {Array} requested the appointments sent to /appointment/add
 * @param {object} response its response ({ data: { result } } or { data: { successes } })
 */
export function meetBookingNotice(requested, response) {
  const asked = (Array.isArray(requested) ? requested : []).some((a) => a && MEET_REQUESTED.has(a.google_meet_enabled));
  if (!asked) return null;
  const data = response && response.data;
  const booked = data && data.result ? [data.result] : (data && Array.isArray(data.successes) ? data.successes : []);
  if (!booked.length) return null;
  const withMeet = booked.filter((a) => a && Number(a.google_meet_enabled) === 1).length;
  if (withMeet === 0) return { type: 'warning', message: MESSAGES.meetNotAdded };
  return { type: 'info', message: withMeet > 1 ? MESSAGES.meetsBeingCreated : MESSAGES.meetBeingCreated };
}

/**
 * A Google calendar id short enough for one line ("11875b46…@group.calendar.google.com"); the
 * full id goes in a tooltip. Ids are not secret — this is only about fitting the card.
 */
export function shortCalendarId(id) {
  const value = typeof id === 'string' ? id : '';
  if (value.length <= 40) return value;
  const at = value.lastIndexOf('@');
  if (at > 0) return `${value.slice(0, Math.min(8, at))}…${value.slice(at)}`;
  return `${value.slice(0, 24)}…`;
}

/** The URL, parsed, when it is https on exactly this origin; otherwise null. */
function httpsUrlOn(url, origin) {
  if (typeof url !== 'string' || url === '') return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && parsed.origin === origin ? parsed : null;
  } catch (err) {
    return null;
  }
}

/** Only Google's own consent screen, over https, is ever navigated to. */
export function isGoogleAuthorizationUrl(url) {
  return httpsUrlOn(url, GOOGLE_AUTHORIZATION_ORIGIN) !== null;
}

/**
 * A Google Meet meeting link: https://meet.google.com/<code>, nothing else. Only such a link is
 * ever offered to join or copy.
 */
export function isGoogleMeetUrl(url) {
  const parsed = httpsUrlOn(url, GOOGLE_MEET_ORIGIN);
  return !!parsed && !parsed.username && !parsed.password && /^\/[A-Za-z0-9_-]+$/.test(parsed.pathname);
}

/** "meet.google.com/abc-defg-hij" — the link as the panel shows it. */
export function meetLinkLabel(url) {
  if (!isGoogleMeetUrl(url)) return '';
  const parsed = new URL(url);
  return `${parsed.host}${parsed.pathname}`;
}

// ─── One appointment's Google Meet (the appointment detail panel) ───

/** This appointment's entry in a /appointment/google-meet/get answer, or null. */
export function pickAppointmentMeet(data, appointmentId) {
  const list = data && Array.isArray(data.appointments) ? data.appointments : [];
  return list.find((a) => a && String(a.appointment_id) === String(appointmentId)) || null;
}

// How long the panel keeps checking on a Meet that is being made, and how often. Making one
// usually takes a few seconds; when Google is slow the backend tries again on its own, so after
// MEET_WAIT_MS the panel stops asking and offers "Check again" instead.
export const MEET_WAIT_MS = 2 * 60 * 1000;

/** The wait before checking again, `elapsedMs` into a wait — false once it is time to stop. */
export function meetRefreshDelay(elapsedMs) {
  const elapsed = Number(elapsedMs) || 0;
  if (elapsed < 15 * 1000) return 3 * 1000;
  if (elapsed < 45 * 1000) return 10 * 1000;
  if (elapsed < MEET_WAIT_MS) return 30 * 1000;
  return false;
}

// Enable is only for an upcoming appointment (the API's own rule); retry and remove while the
// appointment can still happen.
const UPCOMING_APPOINTMENT = ['BOOKED', 'PENDING_PAYMENT'];
const FINISHED_APPOINTMENT = ['CANCELLED', 'COMPLETED', 'NO_SHOW'];

/**
 * What an appointment's detail panel shows about its Google Meet, and which actions it offers.
 * Buttons are only what this user may try: the API checks every action again itself.
 *
 * @param {object} input
 *   meet        the appointment's entry from /appointment/google-meet/get
 *   appointment { status, date } — date YYYY-MM-DD when known
 *   today       YYYY-MM-DD
 *   connection  the clinic's Google Calendar status (as the status API returns it), null while unknown
 *   canManage   may change this appointment's scheduling (manage_scheduling)
 *   slow        a Meet being made has taken longer than the panel keeps checking (MEET_WAIT_MS)
 * @returns {{ visible: boolean, state: string, message: string|null, meetUrl: string|null, linkLabel: string|null,
 *   enable: boolean, retry: boolean, remove: boolean, reconnectLink: boolean }}
 *   state: hidden | not_enabled | unavailable | needs_reconnect | pending | ready | failed | waiting |
 *          link_unavailable | other_account | cancelled
 */
export function appointmentMeetView({ meet, appointment = {}, today = null, connection = null, canManage = false, slow = false } = {}) {
  const hidden = {
    visible: false, state: 'hidden', message: null, meetUrl: null, linkLabel: null,
    enable: false, retry: false, remove: false, reconnectLink: false,
  };
  const view = (state, message, extra = {}) => ({ ...hidden, visible: true, state, message, ...extra });
  if (!meet) return hidden;

  const status = appointment.status || 'BOOKED';
  const manage = canManage === true;
  const changeable = manage && !FINISHED_APPOINTMENT.includes(status);
  const upcoming = UPCOMING_APPOINTMENT.includes(status) && !(appointment.date && today && String(appointment.date) < String(today));
  const conn = connection && typeof connection === 'object' ? connection : null;
  const connectionStatus = conn ? conn.status : null;
  // Reconnecting is offered only to someone who may manage the clinic's Google connection.
  const reconnect = (extra) => view('needs_reconnect',
    conn && conn.can_manage === true ? MESSAGES.meetNeedsReconnect : MESSAGES.meetAskAdminToReconnect,
    { reconnectLink: !!conn && conn.can_manage === true, ...extra });

  if (meet.google_meet_enabled !== true) {
    // Nothing to say on a cancelled appointment, nor where the integration is switched off.
    if (status === 'CANCELLED' || (conn && conn.enabled === false)) return hidden;
    if (!conn) return view('not_enabled', MESSAGES.meetNotEnabled);
    if (!conn.available) return view('unavailable', MESSAGES.meetUnavailable);
    switch (connectionStatus) {
      case 'CONNECTED':
        return view('not_enabled', MESSAGES.meetNotEnabled, { enable: manage && upcoming });
      case 'NEEDS_RECONNECT':
        return reconnect();
      case 'DISCONNECTING':
        return view('unavailable', MESSAGES.meetDisconnecting);
      default:
        return view('unavailable', MESSAGES.meetNotConnected);
    }
  }

  // The backend removes a cancelled appointment's event itself: nothing to retry or remove.
  if (status === 'CANCELLED') return view('cancelled', MESSAGES.meetAppointmentCancelled);

  const pending = ({ retryable }) => view('pending', slow ? MESSAGES.meetSlow : MESSAGES.meetCreating, {
    retry: slow && retryable && changeable,
    remove: changeable,
  });

  switch (meet.state) {
    case 'SYNCED':
      if (isGoogleMeetUrl(meet.meet_url)) {
        return view('ready', MESSAGES.meetReady, { meetUrl: meet.meet_url, linkLabel: meetLinkLabel(meet.meet_url), remove: changeable });
      }
      return view('link_unavailable', MESSAGES.meetLinkUnavailable, { remove: changeable });
    case 'FAILED': {
      // Made too late: trying again cannot help.
      const over = meet.last_error === 'appointment_passed';
      return view('failed', over ? MESSAGES.meetFailedAppointmentOver : MESSAGES.meetFailed, { retry: changeable && !over, remove: changeable });
    }
    case 'WAITING_FOR_CONNECTION':
      // Switched off or unavailable on this server: nothing is made, whatever the connection says.
      if (conn && (conn.enabled === false || !conn.available)) return view('unavailable', MESSAGES.meetUnavailable, { remove: changeable });
      if (connectionStatus === 'CONNECTED') return pending({ retryable: true });
      if (connectionStatus === 'NEEDS_RECONNECT' || (!conn && meet.last_error === 'needs_reconnect')) return reconnect({ remove: changeable });
      if (!conn) return view('waiting', MESSAGES.meetWaitingForConnection, { remove: changeable });
      if (connectionStatus === 'DISCONNECTING') return view('unavailable', MESSAGES.meetDisconnecting, { remove: changeable });
      return view('unavailable', MESSAGES.meetNotConnected, { remove: changeable });
    case 'DETACHED':
      return view('other_account', MESSAGES.meetOtherAccount, { remove: changeable });
    case 'PENDING':
      return pending({ retryable: true });
    case 'NONE':
      // On, and about to be made: there is no event to retry yet.
      return pending({ retryable: false });
    default:
      return view('link_unavailable', MESSAGES.meetLinkUnavailable, { remove: changeable });
  }
}

// What an enable / retry / disable refusal from the API means (AdminGoogleMeetController codes).
const MEET_ACTION_CODES = {
  disabled: MESSAGES.meetUnavailable,
  not_configured: MESSAGES.meetUnavailable,
  google_not_connected: MESSAGES.meetNotConnected,
  appointment_not_found: 'This appointment could not be found.',
  appointment_not_upcoming: 'Google Meet can only be added to an upcoming, booked appointment.',
  appointment_passed: 'Google Meet cannot be added to an appointment that is already over.',
  nothing_to_retry: 'There is nothing to retry for this appointment.',
};
const MEET_ACTION_FAILED = {
  enable: MESSAGES.meetEnableFailed,
  retry: MESSAGES.meetRetryFailed,
  disable: MESSAGES.meetRemoveFailed,
};

/**
 * An appointment's Google Meet action failed (action: enable | retry | disable). null when the app
 * already shows its own notice: demo mode, or a subscription that is read-only or has lapsed (402).
 */
export function meetActionErrorMessage(err, action) {
  if (isNetworkError(err)) return MESSAGES.unreachable;
  const body = err && err.response ? err.response.data : err;
  if (body && body.is_demo) return null;
  const status = httpStatus(err);
  if (status === 402) return null;
  const code = apiCode(err);
  if (code && Object.prototype.hasOwnProperty.call(MEET_ACTION_CODES, code)) return MEET_ACTION_CODES[code];
  if (status === 403) return MESSAGES.meetNoPermission;
  if (status === 503) return MESSAGES.meetUnavailable;
  return MEET_ACTION_FAILED[action] || MESSAGES.meetEnableFailed;
}

/** Send the browser to Google's consent screen (the whole tab — never a frame or a popup). */
export function redirectToGoogle(url) {
  window.location.href = url;
}
